import Lottie from "lottie-react";
import { Clock, Gift, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import useApiRequest from "../../../lib/hooks/useRequest";
import { ProvisionNotificationDataResponseDto, ProvisionNotificationResponseDto } from "../../../lib/interfaces/bonus";
import { Button } from "../../../ui/components/ui/button";
import DataFetchParent from "../../../ui/components/ui/data-fetch";
import { Dialog, DialogContent, DialogTitle } from "../../../ui/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../ui/components/ui/tooltip";

const EmployeeNotifications = () => {
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState<ProvisionNotificationResponseDto>();
  const { apiRequest } = useApiRequest();

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    const response = await apiRequest<ProvisionNotificationResponseDto>("provision/employee/notifications", "GET");
    if (response.data) {
      setNotifications(response.data);
    }
    setLoading(false);
  }, [apiRequest]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const loadAnimationData = useCallback(
    async (animationUrl: string) => {
      try {
        // Extract filename from S3 URL
        const filename = animationUrl.split("/").pop();
        if (!filename) throw new Error("Invalid animation URL");

        // Try to load via backend proxy endpoint
        const response = await apiRequest<any>(`animations/${filename}`, "GET");
        return response.data;
      } catch (error) {
        console.error("Failed to load via proxy, trying direct URL...", error);
        // Fallback to direct fetch if proxy doesn't exist yet
        const directResponse = await fetch(animationUrl);
        if (!directResponse.ok) {
          throw new Error("Failed to load animation");
        }
        return await directResponse.json();
      }
    },
    [apiRequest]
  );

  const onAnimationFinished = useCallback(
    async (notificationIds: string[]) => {
      console.log(`Animation finished for notification: ${notificationIds}`);

      await apiRequest("provision/employee/notifications", "PATCH", {
        body: {
          provisionIds: notificationIds,
        },
      });
    },
    [apiRequest]
  );

  return (
    <DataFetchParent
      isLoading={loading}
      data={notifications}
      renderElement={(data: ProvisionNotificationResponseDto) => {
        return (
          <div className="relative">
            <BonusNotificationSystem
              notifications={data}
              onAnimationFinished={onAnimationFinished}
              loadAnimationData={loadAnimationData}
            />
          </div>
        );
      }}
    />
  );
};

interface BonusNotificationSystemProps {
  notifications: ProvisionNotificationResponseDto;
  onAnimationFinished: (notificationId: string[]) => void;
  loadAnimationData: (animationUrl: string) => Promise<any>;
}

const BonusNotificationSystem: React.FC<BonusNotificationSystemProps> = ({
  notifications,
  onAnimationFinished,
  loadAnimationData,
}) => {
  const [currentAnimationIndex, setCurrentAnimationIndex] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const [showQueue, setShowQueue] = useState(false);
  const [dialogManuallyClosed, setDialogManuallyClosed] = useState(false);

  const hasNewProvisions = useMemo(
    () =>
      notifications.newProvisions &&
      notifications.provisionNotificationData &&
      notifications.provisionNotificationData.length > 0,
    [notifications.newProvisions, notifications.provisionNotificationData]
  );

  const animationsQueue = notifications.provisionNotificationData || [];

  // Start showing animations when new provisions are detected
  useEffect(() => {
    if (hasNewProvisions && !showDialog && !dialogManuallyClosed) {
      setShowDialog(true);
      setCurrentAnimationIndex(0);
      setShowQueue(true);
    }
  }, [hasNewProvisions, showDialog, dialogManuallyClosed]);

  // Reset manual close flag when new notifications arrive
  useEffect(() => {
    if (hasNewProvisions) {
      setDialogManuallyClosed(false);
    }
  }, [hasNewProvisions]);

  const currentAnimation = hasNewProvisions ? animationsQueue[currentAnimationIndex] : null;

  const handleAnimationComplete = () => {
    if (!currentAnimation) return;

    // Trigger the callback
    onAnimationFinished(currentAnimation.provisionIds);

    // Move to next animation or close dialog
    if (currentAnimationIndex < animationsQueue.length - 1) {
      setCurrentAnimationIndex((prev) => prev + 1);
      setAnimationKey((prev) => prev + 1); // Force re-render of animation
    } else {
      // All animations completed
      setShowDialog(false);
      setShowQueue(false);
      setCurrentAnimationIndex(0);
      setDialogManuallyClosed(true); // Mark as manually closed
    }
  };

  const handleDialogClose = () => {
    if (currentAnimation) {
      onAnimationFinished(currentAnimation.provisionIds);
    }
    setShowDialog(false);
    setShowQueue(false);
    setCurrentAnimationIndex(0);
    setDialogManuallyClosed(true); // Mark as manually closed
  };

  const handleSkipToNext = () => {
    handleAnimationComplete();
  };

  const handleSkipAll = () => {
    // Trigger callback for all remaining animations
    for (let i = currentAnimationIndex; i < animationsQueue.length; i++) {
      onAnimationFinished(animationsQueue[i].provisionIds);
    }
    setShowDialog(false);
    setShowQueue(false);
    setCurrentAnimationIndex(0);
    setDialogManuallyClosed(true); // Mark as manually closed
  };

  return (
    <>
      {/* Notification Queue Bar */}
      {showQueue && (
        <NotificationQueueBar
          notifications={animationsQueue}
          currentIndex={currentAnimationIndex}
          onClose={() => setShowQueue(false)}
        />
      )}

      {/* Bonus Animation Dialog */}
      <BonusAnimationDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        animation={currentAnimation}
        animationKey={animationKey}
        currentIndex={currentAnimationIndex}
        totalCount={animationsQueue.length}
        onAnimationComplete={handleAnimationComplete}
        onClose={handleDialogClose}
        onSkipToNext={handleSkipToNext}
        onSkipAll={handleSkipAll}
        loadAnimationData={loadAnimationData}
      />
    </>
  );
};

interface NotificationQueueBarProps {
  notifications: ProvisionNotificationDataResponseDto[];
  currentIndex: number;
  onClose: () => void;
}

const NotificationQueueBar: React.FC<NotificationQueueBarProps> = ({ notifications, currentIndex, onClose }) => {
  return (
    <div className="fixed top-4 right-4 z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-80">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Gift className="w-5 h-5 text-yellow-500" />
          <h3 className="font-semibold text-sm">Bonus Benachrichtigungen</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="space-y-2">
        {notifications.map((notification, index) => (
          <TooltipProvider key={index}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className={`flex items-center gap-3 p-2 rounded-md transition-colors ${
                    index === currentIndex
                      ? "bg-yellow-50 border border-yellow-200"
                      : index < currentIndex
                      ? "bg-green-50 border border-green-200"
                      : "bg-gray-50 border border-gray-200"
                  }`}
                >
                  <div
                    className={`w-2 h-2 rounded-full ${
                      index === currentIndex
                        ? "bg-yellow-500 animate-pulse"
                        : index < currentIndex
                        ? "bg-green-500"
                        : "bg-gray-400"
                    }`}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{notification.provisionAmount}€ Provision</div>
                    <div className="text-xs text-gray-500">
                      {new Date(notification.provisionDate).toLocaleDateString("de-DE")}
                    </div>
                  </div>
                  {index === currentIndex && <Clock className="w-4 h-4 text-yellow-500" />}
                  {index < currentIndex && (
                    <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full" />
                    </div>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-xs">
                  {index === currentIndex
                    ? "Wird aktuell angezeigt"
                    : index < currentIndex
                    ? "Abgeschlossen"
                    : "Wartet auf Anzeige"}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}
      </div>

      <div className="mt-3 text-xs text-gray-500 text-center">
        {currentIndex + 1} von {notifications.length} Benachrichtigungen
      </div>
    </div>
  );
};

interface BonusAnimationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  animation: ProvisionNotificationDataResponseDto | null;
  animationKey: number;
  currentIndex: number;
  totalCount: number;
  onAnimationComplete: () => void;
  onClose: () => void;
  onSkipToNext: () => void;
  onSkipAll: () => void;
  loadAnimationData: (animationUrl: string) => Promise<any>;
}

const BonusAnimationDialog: React.FC<BonusAnimationDialogProps> = ({
  open,
  onOpenChange,
  animation,
  animationKey,
  currentIndex,
  totalCount,
  onAnimationComplete,
  onClose,
  onSkipToNext,
  onSkipAll,
  loadAnimationData,
}) => {
  const [animationData, setAnimationData] = useState<any>(null);
  const [loadingAnimation, setLoadingAnimation] = useState(false);
  const [animationError, setAnimationError] = useState<string | null>(null);

  // Load animation data from URL
  useEffect(() => {
    if (!animation?.bonusAnimationUrl) return;

    setLoadingAnimation(true);
    setAnimationError(null);

    // Try to load via backend proxy first, fallback to direct URL
    const loadAnimation = async () => {
      try {
        const response = await fetch(animation.bonusAnimationUrl);

        if (!response.ok) {
          throw new Error("Failed to load animation");
        }

        const data = await response.json();
        setAnimationData(data);
        setLoadingAnimation(false);
      } catch (error) {
        console.error("Error loading animation:", error);
        setAnimationError("Animation konnte nicht geladen werden");
        setLoadingAnimation(false);
      }
    };

    loadAnimation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animation?.bonusAnimationUrl, animationKey]);

  const handleDialogChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose();
    }
    onOpenChange(isOpen);
  };

  if (!animation) return null;

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="max-w-2xl h-[700px] p-0 overflow-hidden">
        <div className="relative w-full h-full flex flex-col">
          {/* Header */}
          <DialogTitle>
            <div className="p-6 border-b bg-gradient-to-r from-[#C0DE60] to-[#5FD5DE]">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">🎉 Glückwunsch!</h2>
                  <p className="text-gray-600 mt-1">Du hast {animation.provisionAmount}€ Provision erhalten!</p>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">
                    {currentIndex + 1} von {totalCount}
                  </div>
                  <div className="text-xs text-gray-400">
                    {new Date(animation.provisionDate).toLocaleDateString("de-DE")}
                  </div>
                </div>
              </div>
            </div>
          </DialogTitle>

          {/* Animation Content */}
          <div className="flex-1 flex items-center justify-center bg-white">
            {loadingAnimation ? (
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
                <p className="text-gray-500">Lade Animation...</p>
              </div>
            ) : animationError ? (
              <div className="text-center text-red-500">
                <p>{animationError}</p>
                <Button onClick={onAnimationComplete} className="mt-4">
                  Weiter
                </Button>
              </div>
            ) : animationData ? (
              <Lottie
                key={animationKey}
                animationData={animationData}
                loop={false}
                className="w-full h-full max-w-md max-h-md"
                onComplete={onAnimationComplete}
              />
            ) : (
              <div className="text-center text-gray-500">
                <p>Keine Animation verfügbar</p>
                <Button onClick={onAnimationComplete} className="mt-4">
                  Weiter
                </Button>
              </div>
            )}
          </div>
          {/* Footer Controls */}
          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                {totalCount > 1 && currentIndex < totalCount - 1 && (
                  <Button variant="outline" onClick={onSkipToNext} className="text-sm">
                    Nächste
                  </Button>
                )}
                {totalCount > 1 && currentIndex < totalCount - 1 && (
                  <Button variant="outline" onClick={onSkipAll} className="text-sm">
                    Alle überspringen
                  </Button>
                )}
              </div>
              <Button onClick={onClose} variant="secondary">
                Schließen
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeNotifications;
